import React from 'react';
import styled from '@emotion/native';
import { Feather } from '@expo/vector-icons';
import { useQuery } from '@apollo/client';
import { ActivityIndicator } from 'react-native-paper';
import useBreakpoint from '@/shared/hooks/useBreakpoint';
import { IsDesktopProps } from '@/shared/styles/types';
import ScrollableView from '@/shared/components/ScrollableView';
import Button from '@/shared/components/Button';
import { BodyText } from '@/shared/components/StyledText';
import { sand, green, white } from '@/shared/styles/colors';
import { EMME_WEB_AVAILABILITY_URL } from '@/shared/constants/utility';
import { handleUrlOpen } from '@/shared/utils';
import { GET_STYLIST_SCHEDULING_DATA } from '@/shared/queries/stylistSchema';
import { QueryResult } from '@/shared/types/generated';
import { isCalendarSetupComplete } from '@/shared/components/schedule/utils';
import AvailabilityManager from '@/shared/components/schedule/AvailabilityManager';

const contentContainerStyleLoading = {
  alignItems: 'center' as const,
  justifyContent: 'center' as const,
  height: '100%' as const,
};

const contentContainerStyleCenter = {
  alignItems: 'center' as const,
  height: '100%' as const,
};

const contentContainerStyleStretch = {
  alignItems: 'stretch' as const,
  height: '100%' as const,
};

/**
 * Mobile availability: show Schedule step only when calendar is already set up (desktop).
 */
const Availability = () => {
  const { isDesktop } = useBreakpoint();

  const {
    data: { schedulingData } = {},
    loading,
    error,
    refetch,
  } = useQuery<QueryResult<'schedulingData'>>(GET_STYLIST_SCHEDULING_DATA, {
    fetchPolicy: 'network-only',
  });

  const onOpenWeb = () => {
    handleUrlOpen(EMME_WEB_AVAILABILITY_URL);
  };

  const calendarSetUp = isCalendarSetupComplete(schedulingData);
  const showGoToWebMessage = !calendarSetUp;

  if (loading) {
    return (
      <ScrollableView
        scrollEnabled
        contentContainerStyle={contentContainerStyleLoading}
      >
        <LoadingContainer>
          <ActivityIndicator size="large" color={green} />
        </LoadingContainer>
      </ScrollableView>
    );
  }

  if (error) {
    return (
      <ScrollableView
        scrollEnabled
        contentContainerStyle={contentContainerStyleCenter}
      >
        <Container isDesktop={isDesktop}>
          <MessageContainer>
            <Title>UNABLE TO LOAD</Title>
            <BodyText style={bodyStyle}>
              We couldn&apos;t load your availability. Please try again or set
              your schedule on the EMME website.
            </BodyText>
          </MessageContainer>
          <ErrorActions>
            <Button
              text="TRY AGAIN"
              onPress={() => refetch()}
              variant="primary"
              customStyle={buttonStyle}
            />
            <Button
              text="GO TO WEBSITE"
              onPress={onOpenWeb}
              variant="secondary"
              rightIcon={
                <Feather name="external-link" size={18} color={green} />
              }
              customStyle={buttonStyle}
            />
          </ErrorActions>
        </Container>
      </ScrollableView>
    );
  }

  if (showGoToWebMessage) {
    return (
      <ScrollableView
        scrollEnabled
        contentContainerStyle={contentContainerStyleCenter}
      >
        <Container isDesktop={isDesktop}>
          <MessageContainer>
            <Title>SET AVAILABILITY</Title>
            <BodyText style={bodyStyle}>
              Availability management is currently only available on the web.
              Log in to the EMME website to set or update your schedule.
            </BodyText>
          </MessageContainer>
          <Button
            text="GO TO WEBSITE"
            onPress={onOpenWeb}
            variant="primary"
            rightIcon={<Feather name="external-link" size={18} color={white} />}
            customStyle={buttonStyle}
          />
        </Container>
      </ScrollableView>
    );
  }

  return (
    <ScrollableView
      scrollEnabled
      contentContainerStyle={contentContainerStyleStretch}
    >
      <ScheduleWrapper>
        <AvailabilityManager
          scheduleOnly
          schedulingData={schedulingData}
          schedulingDataLoading={loading}
          onRefetchSchedulingData={refetch}
        />
      </ScheduleWrapper>
    </ScrollableView>
  );
};

const Container = styled.View<IsDesktopProps>(({ isDesktop }) => ({
  flexGrow: 1,
  backgroundColor: sand,
  marginHorizontal: isDesktop ? 48 : 0,
  paddingTop: isDesktop ? 34 : 24,
  paddingBottom: isDesktop ? 40 : 28,
  marginTop: isDesktop ? 14 : 0,
  marginBottom: isDesktop ? 34 : 0,
  alignSelf: 'stretch',
  alignItems: 'center',
  borderRadius: isDesktop ? 8 : 0,
  justifyContent: 'center',
  paddingHorizontal: 24,
}));

/** Full-width wrapper for the schedule view so the calendar has no side padding. Matches home (sessions) top spacing. */
const ScheduleWrapper = styled.View({
  flexGrow: 1,
  width: '100%',
  alignSelf: 'stretch',
  backgroundColor: sand,
  paddingTop: 12,
});

const MessageContainer = styled.View({
  alignItems: 'center',
  marginBottom: 32,
});

const ErrorActions = styled.View({
  width: '100%',
  gap: 12,
});

const Title = styled.Text({
  fontFamily: 'Lora',
  fontSize: 28,
  lineHeight: 36,
  color: green,
  marginBottom: 20,
  textAlign: 'center',
  letterSpacing: 0.5,
});

const LoadingContainer = styled.View({
  flex: 1,
  justifyContent: 'center',
  alignItems: 'center',
});

const bodyStyle = { textAlign: 'center' as const };
const buttonStyle = { width: '100%' as const };

export default Availability;
